/*!
 * mos-search-restore.js — Mega Office Supplies
 * -----------------------------------------------------------------------------
 * Corrections to the theme's own predictive-search dropdown. Loaded by
 * sections/mos-quote-only.liquid, after assets/predictive-search.js.
 *
 * Fixes
 *   1. Backspacing the box back to empty left the dropdown blank and closed.
 *      The theme only builds the empty/trending view from onFocus, and the box
 *      is already focused when you backspace, so nothing rebuilt it.
 *   2. Clicking away wiped whatever you had typed (closeResults(true) sets
 *      input.value = ""), so coming back to the box started from nothing.
 *
 * Nothing in the theme's predictive-search.js is edited. onChange, close,
 * closeResults and renderSearchResults are reachable on the prototype; onFocus,
 * onFocusOut and onKeyup are bound copies taken in the constructor and cannot
 * be patched, so those are handled with capture-phase listeners instead.
 *
 * Every behaviour is behind its own flag in window.mosSearchRestoreConfig.
 * window.__mosSearchRestore exposes counters for diagnosis.
 */
(function () {
  "use strict";
  if (window.__mosSearchRestore) return;

  var CFG = window.mosSearchRestoreConfig || {};
  if (CFG.enabled === false) return;

  var RESTORE = CFG.restoreEmptyState !== false;   // fix 1
  var KEEPTERM = CFG.keepTermOnClose !== false;    // fix 2
  var DEBUG = CFG.debug === true;

  var API = (window.__mosSearchRestore = {
    version: 1,
    restoreEmptyState: RESTORE,
    keepTermOnClose: KEEPTERM,
    cached: 0,          // times the empty-state markup was captured
    restoredFromCache: 0,
    rehydrated: 0,      // fell back to the theme's own fetch
    suppressedCloses: 0,
    termsKept: 0,
    patched: false,
    lastError: null
  });

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ["[mos-search-restore]"].concat([].slice.call(arguments)));
  }

  function focusedIn(el) {
    var a = document.activeElement;
    return !!(a && (a === el || el.contains(a)));
  }

  /* The element may not be upgraded yet: sections/mos-quote-only.liquid sits in
     the footer group, which Shopify renders BEFORE the header's search section,
     so this deferred script executes before assets/predictive-search.js calls
     customElements.define. Object.getPrototypeOf on a not-yet-upgraded element
     returns HTMLElement.prototype — patching that would be both useless and
     destructive, so check the methods are actually there first. */
  function isPredictiveProto(proto) {
    return !!proto &&
      typeof proto.onChange === "function" &&
      typeof proto.close === "function" &&
      typeof proto.closeResults === "function" &&
      typeof proto.renderSearchResults === "function" &&
      typeof proto.maybeHydrateSearchspringInitial === "function";
  }

  function patch(proto) {
    if (!isPredictiveProto(proto) || proto.__mosSearchRestorePatched) return false;
    proto.__mosSearchRestorePatched = true;

    var origRender = proto.renderSearchResults;
    var origChange = proto.onChange;
    var origClose = proto.close;
    var origCloseResults = proto.closeResults;

    /* Remember the empty-query (trending) markup the first time the theme
       builds it, so we can put it straight back without another round trip. */
    proto.renderSearchResults = function (markup) {
      try {
        if (typeof markup === "string" && markup && !this.getQuery().length) {
          this.__mosInitialMarkup = markup;
          API.cached++;
        }
      } catch (e) { API.lastError = String(e); }
      return origRender.apply(this, arguments);
    };

    /* Backspace to empty: rebuild the initial view instead of closing blank. */
    proto.onChange = function () {
      try {
        /* Escape natively clears a type="search" box and fires an input
           event. That must stay a close, not a re-open. */
        var escaping = this.__mosLastKeyDown === "Escape" &&
                       Date.now() - (this.__mosKeyDownAt || 0) < 600;
        if (RESTORE && !escaping && !this.getQuery().length && focusedIn(this)) {
          this.searchTerm = "";
          if (this.__mosInitialMarkup) {
            this._searchspringInitialHydrated = true;
            this.renderSearchResults(this.__mosInitialMarkup);
            try { this.clickSearchTabs(); } catch (e) {}
            try { this.initProductsSlider(); } catch (e) {}
            try { this.calcHeight(); } catch (e) {}
            API.restoredFromCache++;
            log("restored empty state from cache");
            return;
          }
          /* Never captured it (first interaction was a paste, or the first
             fetch failed) — ask the theme to build it the normal way. */
          this._searchspringInitialHydrated = false;
          this._searchspringInitialPending = false;
          if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = "";
          this.maybeHydrateSearchspringInitial();
          API.rehydrated++;
          log("no cached markup, re-hydrating");
          return;
        }
      } catch (e) { API.lastError = String(e); }
      return origChange.apply(this, arguments);
    };

    /* onKeyup (a bound copy we cannot replace) runs
         this.getQuery().length || this.close(true)
       before anything else, so the box blanks on the keystroke that empties
       it. Swallow that one call — and only that one. */
    proto.close = function (clearSearchTerm) {
      try {
        if (RESTORE && clearSearchTerm === true && this.__mosInitialMarkup &&
            !this.getQuery().length && focusedIn(this) &&
            this.__mosLastKey !== "Escape" &&
            Date.now() - (this.__mosKeyupAt || 0) < 60) {
          API.suppressedCloses++;
          log("suppressed the pre-emptive close on keyup");
          return;
        }
      } catch (e) { API.lastError = String(e); }
      return origClose.apply(this, arguments);
    };

    /* Click away: close the dropdown but leave the typed term in the box, so
       returning to it does not start from nothing. Escape and form reset still
       clear, because those do not come through a focusout. */
    proto.closeResults = function (clearSearchTerm) {
      try {
        if (KEEPTERM && clearSearchTerm === true &&
            Date.now() - (this.__mosBlurAt || 0) < 500 &&
            this.getQuery().length) {
          API.termsKept++;
          log("kept the typed term on close");
          return origCloseResults.call(this, false);
        }
      } catch (e) { API.lastError = String(e); }
      return origCloseResults.apply(this, arguments);
    };

    API.patched = true;
    return true;
  }

  /* Capture-phase markers. These run before the element's own bound handlers,
     which is the only way to tell the patched methods above why they are being
     called. They observe; they do not stop the event. */
  function markers(el) {
    if (el.__mosSearchRestoreMarked) return;
    el.__mosSearchRestoreMarked = true;
    el.addEventListener("keydown", function (e) {
      el.__mosLastKeyDown = e && e.code;
      el.__mosKeyDownAt = Date.now();
    }, true);
    el.addEventListener("keyup", function (e) {
      el.__mosLastKey = e && e.code;
      el.__mosKeyupAt = Date.now();
    }, true);
    el.addEventListener("focusout", function () {
      el.__mosBlurAt = Date.now();
    }, true);
  }

  function init() {
    var hosts = document.querySelectorAll("predictive-search");
    for (var i = 0; i < hosts.length; i++) {
      markers(hosts[i]);
      patch(Object.getPrototypeOf(hosts[i]));
    }
    if (!API.patched && window.customElements && customElements.get) {
      var C = customElements.get("predictive-search");
      if (C) patch(C.prototype);
    }
    return API.patched;
  }

  /* Any search box focused later gets its markers before it can emit a keyup. */
  document.addEventListener("focusin", function (e) {
    var t = e && e.target;
    var host = t && t.closest && t.closest("predictive-search");
    if (host) markers(host);
  }, true);

  if (!init()) {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined("predictive-search").then(function () { setTimeout(init, 0); });
    }
    var tries = 0;
    var t = setInterval(function () {
      if (init() || ++tries > 150) clearInterval(t);
    }, 60);
  }
})();
